import { useEffect } from "react";
import { EdgesGeometry, LineDashedMaterial, LineSegments } from "three";
import type { Object3D, Mesh, Material } from "three";
import { findLayerInfo, type MeshKind } from "@/lib/model-layers";
import type { ViewMode } from "./ViewModeBar";

const EDGE_KINDS = new Set<MeshKind>(["SLAB", "WALL", "WALL_EXT", "WALL_INT", "FACADE", "ROOF"]);

type MaterialProps = { transparent?: boolean; opacity?: number; depthWrite?: boolean };

function setMaterialProps(mesh: Mesh, props: MaterialProps) {
  const mat = mesh.material;
  if (!mat) return;
  for (const m of Array.isArray(mat) ? mat : [mat]) Object.assign(m, props);
}

interface FloorVisibilityProps {
  modelRoot: Object3D | null;
  viewMode: ViewMode;
  showScaffold: boolean;
  floorDates?: Map<string, string>;
  viewDate?: string | null;
  hiddenBuildings?: Set<string>;
  hiddenFloors?: Set<string>;
}

export function FloorVisibility({
  modelRoot,
  viewMode,
  showScaffold,
  floorDates,
  viewDate,
  hiddenBuildings,
  hiddenFloors,
}: FloorVisibilityProps) {
  useEffect(() => {
    if (!modelRoot) return;

    modelRoot.traverse((obj) => {
      if (obj.userData.__isProgressEdge) return;

      const mesh = obj as Mesh;
      if (!mesh.material) return;
      const info = findLayerInfo(obj);

      mesh.visible = true;
      setMaterialProps(mesh, { transparent: false, opacity: 1, depthWrite: true });
      obj.castShadow = true;
      const prevEdges = obj.userData.__edges as LineSegments | undefined;
      if (prevEdges) prevEdges.visible = false;

      if (info.kind === "SAFETY_NET") {
        if (!showScaffold) {
          mesh.visible = false;
          return;
        }
        setMaterialProps(mesh, { transparent: true, opacity: 0.35, depthWrite: false });
      }

      if (info.buildingId) {
        if (hiddenBuildings?.has(info.buildingId)) {
          mesh.visible = false;
          return;
        }
        if (info.floor && hiddenFloors?.has(`${info.buildingId}/${info.floor}`)) {
          mesh.visible = false;
          return;
        }
      }

      if (viewMode === "progress") {
        if (!info.floor || !info.buildingId) return;
        const date = floorDates?.get(`${info.buildingId}/${info.floor}`);
        if (date && viewDate && date > viewDate) {
          const m = obj as Mesh;
          m.castShadow = false;

          if (!EDGE_KINDS.has(info.kind)) {
            mesh.visible = false;
            return;
          }

          if (!m.isMesh || !m.material) return;

          if (!obj.userData.__progressMat) {
            m.material = Array.isArray(m.material)
              ? m.material.map((x) => x.clone())
              : m.material.clone();
            obj.userData.__progressMat = true;
          }
          setMaterialProps(m, { transparent: true, opacity: 0, depthWrite: false });

          let edges = obj.userData.__edges as LineSegments | undefined;
          if (!edges) {
            edges = new LineSegments(
              new EdgesGeometry(m.geometry, 45),
              new LineDashedMaterial({
                color: 0x9ca3af,
                transparent: true,
                opacity: 0.1,
                dashSize: 300,
                gapSize: 200,
              })
            );
            edges.computeLineDistances();
            edges.userData.__isProgressEdge = true;
            edges.renderOrder = 1;
            m.add(edges);
            obj.userData.__edges = edges;
          }
          edges.visible = true;
        }
        return;
      }

      if (viewMode === "xray") {
        setMaterialProps(mesh, { transparent: true, opacity: 0.25, depthWrite: false });
        return;
      }

      if (viewMode === "outside") {
        return;
      }
    });
  }, [modelRoot, viewMode, showScaffold, floorDates, viewDate, hiddenBuildings, hiddenFloors]);

  useEffect(() => {
    const root = modelRoot;
    return () => {
      if (!root) return;
      const stale: Array<[Object3D, LineSegments]> = [];
      root.traverse((o) => {
        const e = o.userData.__edges as LineSegments | undefined;
        if (e) stale.push([o, e]);
      });
      for (const [o, e] of stale) {
        e.geometry.dispose();
        (e.material as Material).dispose();
        o.remove(e);
        delete o.userData.__edges;
      }
    };
  }, [modelRoot]);

  return null;
}
